import React from "react";

import { GoalTemplate } from "./goal_template_class";
import { GoalType } from "./goal_type_class";
import {
  EducationGoalShape,
  EducationLinkShape,
  GoalTypeShape,
  InterviewShape,
  SavePayloadShape,
} from "../interfaces/classTypes";
import { NewEducationLink } from "./new_education_link_class";
import { NewInterview } from "./new_interview_class";

export class EducationGoalTemplate extends GoalTemplate {
  public achievementTarget: number;
  public achievementThreshold: number;
  public educationLinkIds: number[];
  public selectedInterview: InterviewShape | null;
  public selectedEducationLinks: EducationLinkShape[];
  public type: GoalTypeShape;

  constructor({
    achievementTargetFormatted = null,
    achievementTarget = null,
    achievementThreshold = null,
    continuous = true,
    createdAtFormatted = null,
    description = "",
    educationLinkIds = null,
    id = null,
    hasErrors = false,
    leaderboardEnabled = false,
    name = "",
    occurrenceAmount = null,
    occurrenceFrequency = null,
    selectedInterview = null,
    selectedEducationLinks = [],
    user = null,
  }: EducationGoalShape) {
    let extendedData = {
      continuous: continuous,
      createdAtFormatted: createdAtFormatted,
      description: description,
      hasErrors: hasErrors,
      id: id,
      leaderboardEnabled: leaderboardEnabled,
      name: name,
      occurrenceAmount: occurrenceAmount,
      occurrenceFrequency: occurrenceFrequency,
      user: user,
    };
    super(extendedData);

    this.selectedInterview = selectedInterview;
    this.selectedEducationLinks = selectedEducationLinks;
    this.educationLinkIds = this.formatEducationLinkIds(educationLinkIds);

    this.type = GoalType.includedGoalTypes().find(
      (type: GoalTypeShape) => type.id == 2
    );
    this.setEducationLinks(educationLinkIds);
    this.achievementTarget = achievementTargetFormatted
      ? achievementTargetFormatted
      : achievementTarget;
    this.achievementThreshold = achievementThreshold;
  }

  formatEducationLinkIds(educationLinkIds) {
    if (!educationLinkIds) return null;
    if (Array.isArray(educationLinkIds)) return educationLinkIds;
    return educationLinkIds.split(" ").map((id) => parseInt(id));
  }

  setEducationLinks(linkIds) {
    if (!linkIds || this.selectedEducationLinks.length != 0) return;
    var collectedEducationLinks = [];
    var collectedErrors = [];
    this.educationLinkIds.map((id) => {
      let linkObject = NewEducationLink.find_by_id(id);
      if (!linkObject) {
        collectedErrors.push(true);
      } else {
        collectedErrors.push(false);
        collectedEducationLinks.push(linkObject);
      }
    });
    this.hasErrors = collectedErrors.includes(true);
    this.selectedEducationLinks = collectedEducationLinks;
  }

  checkForEditorErrors(interviewIds) {
    let interviews = interviewIds.map((id) => NewInterview.find_by_id(id));

    let targetIds = [];

    interviews.forEach((interview) => {
      interview.stepLinks.forEach((link) => targetIds.push(link.id));
    });
    var collectedErrors = [];

    // Collects an error if the education link's parent interview id doesn't match an interview
    // id from the list of interviews in the scheduling tab.
    if (this.selectedEducationLinks) {
      this.selectedEducationLinks.forEach((link) => {
        if (!targetIds.find((id) => link.id == parseInt(id))) {
          collectedErrors.push(true);
        } else {
          collectedErrors.push(false);
        }
      });
    } else {
      collectedErrors.push(true);
    }

    // Collects an error if original supply of education links cannot find a match in the
    // original list of education links saved to the monitor program.
    if (this.educationLinkIds) {
      this.educationLinkIds.forEach((id) => {
        let linkObject = NewEducationLink.allIncludedNewEducationLinks().find(
          (link) => link.id == id
        );
        if (!linkObject) {
          collectedErrors.push(true);
        } else {
          collectedErrors.push(false);
        }
      });
    }

    this.hasErrors = collectedErrors.includes(true);
    return this.hasErrors;
  }

  formatForSave() {
    var educationLinkIds: number[] | null = this.selectedEducationLinks.map(
      (link) => link.id
    );
    var stringedEducationLinkIds: string = educationLinkIds.join(" ");
    var payload: SavePayloadShape = {
      achievement_condition: "greater_than_equal_to",
      achievement_threshold: this.achievementThreshold,
      achievement_target: this.achievementTarget,
      continuous: this.continuous,
      description: this.description,
      education_link_ids: stringedEducationLinkIds,
      interview_set_ids: null,
      id: this.id,
      leaderboard_enabled: this.leaderboardEnabled,
      measurement_id: null,
      name: this.name,
      occurrence_amount:
        this.occurrenceAmount == null ? 0 : this.occurrenceAmount,
      occurrence_frequency: this.occurrenceFrequency.name,
      type: this.type.name,
      user_id: this.user.id,
    };
    return payload;
  }

  formatForLocalSave() {
    var educationLinkIds: number[] | null = this.selectedEducationLinks.map(
      (link) => link.id
    );
    var stringedEducationLinkIds: string = educationLinkIds.join(" ");
    var payload: any = {
      achievementCondition: "greater_than_equal_to",
      achievementThreshold: this.achievementThreshold,
      achievementTarget: this.achievementTarget,
      continuous: this.continuous,
      createdAtFormatted: this.createdAtFormatted,
      description: this.description,
      educationLinkIds: stringedEducationLinkIds,
      interviewIds: null,
      id: this.id,
      leaderboardEnabled: this.leaderboardEnabled,
      measurementId: null,
      name: this.name,
      occurrenceAmount:
        this.occurrenceAmount == null ? 0 : this.occurrenceAmount,
      occurrenceFrequency: this.occurrenceFrequency.name,
      type: this.type.name,
      user: this.user,
    };
    return payload;
  }

  static complete = (goal: EducationGoalShape) => {
    let results = [
      goal.achievementTarget,
      goal.achievementThreshold,
      goal.occurrenceFrequency,
      goal.continuous ? true : goal.occurrenceAmount,
    ];

    if (
      results.includes(null) ||
      goal.name == "" ||
      goal.selectedEducationLinks.length == 0
    ) {
      return false;
    } else {
      return true;
    }
  };
}
